import { useEffect, useState } from 'react';
import { createQuery, deleteQuery, getUser, updateQueryStatus } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { AUTH_TOKEN_KEY } from '../services/tokenService';
import Navbar from '../components/Navbar';
import './Dashboard.css';

const Dashboard = () => {
  const { isAuthenticated, logout, user, loading: authLoading } = useAuth();
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [newBrief, setNewBrief] = useState({
    main_topic: '',
    persona: '',
    focus_areas: [],
    exclude_topics: [],
    time_of_day: '08:00:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    focusAreaInput: '',
    excludeTopicInput: ''
  });
  const [showNewBriefForm, setShowNewBriefForm] = useState(false);

  // Add new state for managing tag inputs
  const [focusAreaInput, setFocusAreaInput] = useState('');
  const [excludeTopicInput, setExcludeTopicInput] = useState('');

  // Redirect to login page if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      console.log('User not authenticated - redirecting to login page');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000); // Redirect after 2 seconds to allow the user to see the message
    }
  }, [isAuthenticated, authLoading]);

  // Update queries when user data changes
  useEffect(() => {
    console.log('Dashboard: User data changed', { 
      user: user,
      hasQueries: user && Array.isArray(user.queries),
      queriesCount: user?.queries?.length || 0
    });
    
    if (user) {
      // Ensure queries is always an array, even if it's undefined or null from the API
      const userQueries = Array.isArray(user.queries) ? user.queries : [];
      console.log('Setting queries from user data:', userQueries);
      
      // Add editable state to each query
      const queriesWithState = userQueries.map(query => {
        try {
          // The query data is already in the correct structure, just add UI state
          return {
            ...query,
            isEditing: false,
            editData: {
              main_topic: query.topic.main,
              persona: query.topic.persona,
              focus_areas: query.topic.focus_areas,
              exclude_topics: query.topic.exclude_topics,
              time_of_day: query.schedule.time_of_day,
              timezone: query.schedule.timezone,
              status: query.metadata?.status || 'active'
            }
          };
        } catch (error) {
          console.error('Error processing query:', error);
          console.error('Raw query:', query);
          return {
            query_id: query.query_id,
            topic: {
              main: 'Error loading brief',
              persona: '',
              focus_areas: [],
              exclude_topics: [],
              custom_instructions: ''
            },
            schedule: {
              time_of_day: '08:00:00',
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            },
            isEditing: false,
            editData: {}
          };
        }
      });
      setQueries(queriesWithState);
      setLoading(false);
    } else {
      console.log('No user data available');
      setQueries([]);
    }
  }, [user]);

  // Add a manual refresh function to fetch user data again
  const refreshUserData = async () => {
    try {
      setLoading(true);
      console.log('Manually refreshing user data...');
      const userData = await getUser();
      console.log('Refreshed user data:', userData);
      if (userData) {
        // Ensure queries is always an array, even if it's undefined or null from the API
        const userQueries = Array.isArray(userData.queries) ? userData.queries : [];
        console.log('Processing queries from refreshed data:', userQueries);
        
        // Add editable state to each query
        const queriesWithState = userQueries.map(query => {
          try {
            // The query data is already in the correct structure, just add UI state
            return {
              ...query,
              isEditing: false,
              editData: {
                main_topic: query.topic.main,
                persona: query.topic.persona,
                focus_areas: query.topic.focus_areas,
                exclude_topics: query.topic.exclude_topics,
                time_of_day: query.schedule.time_of_day,
                timezone: query.schedule.timezone,
                status: query.metadata?.status || 'active'
              }
            };
          } catch (error) {
            console.error('Error processing query:', error);
            console.error('Raw query:', query);
            return {
              query_id: query.query_id,
              topic: {
                main: 'Error loading brief',
                persona: '',
                focus_areas: [],
                exclude_topics: [],
                custom_instructions: ''
              },
              schedule: {
                time_of_day: '08:00:00',
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
              },
              isEditing: false,
              editData: {}
            };
          }
        });
        setQueries(queriesWithState);
        console.log('Updated queries from refresh:', queriesWithState);
      } else {
        console.log('No user data returned from refresh');
        setQueries([]);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
      setError('Failed to refresh user data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNewBriefChange = (e) => {
    const { name, value } = e.target;
    setNewBrief(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddFocusArea = (e, isNewBrief = true, queryId = null) => {
    e.preventDefault();
    
    if (isNewBrief) {
      const input = newBrief.focusAreaInput.trim();
      if (input) {
        setNewBrief(prev => ({
          ...prev,
          focus_areas: [...prev.focus_areas, input],
          focusAreaInput: ''
        }));
      }
    } else if (queryId) {
      const query = queries.find(q => q.query_id === queryId);
      if (query && query.editData.focusAreaInput?.trim()) {
        handleEditChange(queryId, 'focus_areas', [
          ...(query.editData.focus_areas || []),
          query.editData.focusAreaInput.trim()
        ]);
        handleEditChange(queryId, 'focusAreaInput', '');
      }
    }
  };

  const handleRemoveFocusArea = (index) => {
    setNewBrief(prev => ({
      ...prev,
      focus_areas: prev.focus_areas.filter((_, i) => i !== index)
    }));
  };

  const handleAddExcludeTopic = (e, isNewBrief = true, queryId = null) => {
    e.preventDefault();
    
    if (isNewBrief) {
      const input = newBrief.excludeTopicInput.trim();
      if (input) {
        setNewBrief(prev => ({
          ...prev,
          exclude_topics: [...prev.exclude_topics, input],
          excludeTopicInput: ''
        }));
      }
    } else if (queryId) {
      const query = queries.find(q => q.query_id === queryId);
      if (query && query.editData.excludeTopicInput?.trim()) {
        handleEditChange(queryId, 'exclude_topics', [
          ...(query.editData.exclude_topics || []),
          query.editData.excludeTopicInput.trim()
        ]);
        handleEditChange(queryId, 'excludeTopicInput', '');
      }
    }
  };

  const handleRemoveExcludeTopic = (index) => {
    setNewBrief(prev => ({
      ...prev,
      exclude_topics: prev.exclude_topics.filter((_, i) => i !== index)
    }));
  };

  const handleEditChange = (queryId, field, value) => {
    setQueries(prev => prev.map(query => 
      query.query_id === queryId 
        ? { 
            ...query, 
            editData: { 
              ...query.editData, 
              [field]: value 
            } 
          }
        : query
    ));
  };

  const toggleEditMode = (queryId) => {
    // If we're already editing something else, save that first
    if (editingId && editingId !== queryId) {
      saveEdit(editingId);
    }
    
    setEditingId(queryId === editingId ? null : queryId);
    
    // Initialize edit data with empty input states
    setQueries(prev => prev.map(query => 
      query.query_id === queryId 
        ? { 
            ...query, 
            editData: { 
              ...query.editData,
              focusAreaInput: '',
              excludeTopicInput: ''
            } 
          }
        : query
    ));
  };

  // Add a function to count active briefs
  const countActiveBriefs = () => {
    return queries.filter(query => query.metadata?.status !== 'inactive').length;
  };

  // Add a function to count inactive briefs
  const countInactiveBriefs = () => {
    return queries.filter(query => query.metadata?.status === 'inactive').length;
  };

  const handleAddQuery = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      // Check if user has reached their active query limit
      const maxQueries = user?.subscription?.limits?.max_queries || 2;
      const activeQueries = countActiveBriefs();
      
      if (activeQueries >= maxQueries) {
        setError(
          <div>
            You have reached your limit of {maxQueries} active briefs. Please <a href="#" onClick={() => setShowNewBriefForm(false)} style={{ color: 'inherit', textDecoration: 'underline' }}>disable an existing brief</a> or <a href="/subscription" style={{ color: 'inherit', textDecoration: 'underline' }}>upgrade your subscription</a>.
          </div>
        );
        setLoading(false);
        return;
      }
      
      // Format the data according to the API schema
      const queryData = {
        topic: {
          main: newBrief.main_topic,
          persona: newBrief.persona,
          focus_areas: Array.isArray(newBrief.focus_areas) ? newBrief.focus_areas : [],
          exclude_topics: Array.isArray(newBrief.exclude_topics) ? newBrief.exclude_topics : [],
          custom_instructions: ""
        },
        schedule: {
          time_of_day: newBrief.time_of_day,
          timezone: newBrief.timezone
        },
        metadata: {
          status: "active"
        }
      };
      
      console.log('Adding new query:', queryData);
      
      // Call API to create query
      const response = await createQuery(queryData);
      console.log('Query created successfully:', response);
      
      // Reset form
      setNewBrief({
        main_topic: '',
        persona: '',
        focus_areas: [],
        exclude_topics: [],
        time_of_day: '08:00:00',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        focusAreaInput: '',
        excludeTopicInput: ''
      });
      setShowNewBriefForm(false);
      
      // Refresh user data to get updated queries
      await refreshUserData();
      
    } catch (error) {
      console.error('Error adding query:', error);
      
      if (error.response && error.response.status === 403) {
        if (error.response.data.message && error.response.data.message.includes('limit')) {
          setError(
            <div>
              You have reached your active brief limit. Please <a href="#" onClick={() => setShowNewBriefForm(false)} style={{ color: 'inherit', textDecoration: 'underline' }}>disable an existing brief</a> or <a href="/subscription" style={{ color: 'inherit', textDecoration: 'underline' }}>upgrade your subscription</a>.
            </div>
          );
        } else {
          setError(error.response.data.message || 'An error occurred while creating the brief.');
        }
      } else if (error.response && error.response.status === 401) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => {
          logout();
        }, 2000);
      } else {
        setError(error.response?.data?.message || 'An error occurred while creating the brief.');
      }
    } finally {
      setLoading(false);
    }
  };

  const saveEdit = async (queryId) => {
    try {
      setLoading(true);
      setError(null);
      
      const queryToUpdate = queries.find(q => q.query_id === queryId);
      if (!queryToUpdate) return;
      
      // Check if we're enabling a previously inactive brief
      const wasInactive = queryToUpdate.metadata?.status === 'inactive';
      const willBeActive = queryToUpdate.editData.status !== 'inactive'; // Default to active if not specified
      
      if (wasInactive && willBeActive) {
        // Check if we've reached the active brief limit
        const maxQueries = user?.subscription?.limits?.max_queries || 2;
        const activeQueries = countActiveBriefs();
        
        if (activeQueries >= maxQueries) {
          setError(
            <div>
              You have reached your limit of {maxQueries} active briefs. Please <a href="#" onClick={() => setEditingId(null)} style={{ color: 'inherit', textDecoration: 'underline' }}>disable an existing brief</a> or <a href="/subscription" style={{ color: 'inherit', textDecoration: 'underline' }}>upgrade your subscription</a>.
            </div>
          );
          setLoading(false);
          return;
        }
      }
      
      // Create the query data in the format expected by the API
      const queryData = {
        topic: {
          main: queryToUpdate.editData.main_topic,
          persona: queryToUpdate.editData.persona,
          focus_areas: Array.isArray(queryToUpdate.editData.focus_areas) ? queryToUpdate.editData.focus_areas : [],
          exclude_topics: Array.isArray(queryToUpdate.editData.exclude_topics) ? queryToUpdate.editData.exclude_topics : [],
          custom_instructions: ""
        },
        schedule: {
          time_of_day: queryToUpdate.editData.time_of_day,
          timezone: queryToUpdate.editData.timezone
        },
        metadata: {
          status: queryToUpdate.editData.status || "active"
        }
      };
      
      // In a real app, you would call an update API here
      // For now, we'll delete the old one and create a new one
      await deleteQuery(queryId);
      console.log('Deleted existing query before update');
      
      // Call API to create query with updated data
      const response = await createQuery(queryData);
      console.log('Query updated successfully:', response);
      
      // Reset editing state
      setEditingId(null);
      
      // Refresh user data to get updated queries
      await refreshUserData();
      
    } catch (error) {
      console.error('Error updating query:', error);
      
      if (error.response && error.response.status === 403) {
        if (error.response.data.message && error.response.data.message.includes('limit')) {
          setError(
            <div>
              You have reached your active brief limit. Please <a href="#" onClick={() => setEditingId(null)} style={{ color: 'inherit', textDecoration: 'underline' }}>disable an existing brief</a> or <a href="/subscription" style={{ color: 'inherit', textDecoration: 'underline' }}>upgrade your subscription</a>.
            </div>
          );
        } else {
          setError(error.response.data.message || 'An error occurred while updating the brief.');
        }
      } else if (error.response && error.response.status === 401) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => {
          logout();
        }, 2000);
      } else {
        setError(error.response?.data?.message || 'An error occurred while updating the brief.');
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = (queryId) => {
    setQueries(prev => prev.map(query => 
      query.query_id === queryId 
        ? { 
            ...query, 
            editData: { ...query } // Reset edit data to original
          }
        : query
    ));
    setEditingId(null);
  };

  const handleRemoveQuery = async (queryId) => {
    try {
      // Ask for confirmation
      if (!window.confirm('Are you sure you want to delete this brief? This action cannot be undone.')) {
        return; // User cancelled
      }
      
      setLoading(true);
      setError(null);
      
      console.log('Removing query with ID:', queryId);
      
      // Call API to delete query
      await deleteQuery(queryId);
      console.log('Query deleted successfully');
      
      // If we were editing this query, reset the editing state
      if (editingId === queryId) {
        setEditingId(null);
      }
      
      // Refresh user data to get updated queries
      await refreshUserData();
      
    } catch (error) {
      console.error('Error removing query:', error);
      
      if (error.response && error.response.status === 401) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => {
          logout();
        }, 2000);
      } else {
        setError(`Failed to delete query: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleQueryStatus = async (queryId, newStatus) => {
    try {
      // If we're enabling a brief, check if we've reached the limit
      if (newStatus === 'active') {
        const maxQueries = user?.subscription?.limits?.max_queries || 2;
        const activeQueries = countActiveBriefs();
        
        if (activeQueries >= maxQueries) {
          setError(
            <div>
              You have reached your limit of {maxQueries} active briefs. Please <a href="#" onClick={() => setEditingId(null)} style={{ color: 'inherit', textDecoration: 'underline' }}>disable an existing brief</a> or <a href="/subscription" style={{ color: 'inherit', textDecoration: 'underline' }}>upgrade your subscription</a>.
            </div>
          );
          return;
        }
        
        // No confirmation needed when enabling a brief
      }
      // No confirmation needed when disabling a brief
      
      setLoading(true);
      setError(null);
      
      console.log('Updating query status:', queryId, newStatus);
      
      // Call API to update query status
      const response = await updateQueryStatus(queryId, newStatus);
      console.log('Query status updated successfully:', response);
      
      // Refresh user data to get updated queries
      await refreshUserData();
      
    } catch (error) {
      console.error('Error updating query status:', error);
      
      if (error.response && error.response.status === 401) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => {
          logout();
        }, 2000);
      } else {
        setError(`Failed to update query status: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // If auth is still loading, show loading state
  if (authLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <h1>Brewing your dashboard...</h1>
          <div className="loading-spinner"></div>
          <p>Preparing your personalized news briefings</p>
          <div className="loading-progress"></div>
        </div>
      </div>
    );
  }

  // If not authenticated, show message
  if (!isAuthenticated) {
    return (
      <div className="auth-required-container">
        <div className="auth-required-content">
          <h1>Authentication Required</h1>
          <p>You need to be logged in to view this page.</p>
          <p>Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Navbar />
      <div className="dashboard-content">
        <header className="dashboard-header">
          <div className="dashboard-header-content">
            <h1>Welcome, {user?.email?.split('@')[0]}</h1>
            <p className="dashboard-subtitle">Your personalized AI-powered news and insights, delivered daily</p>
            <div className="subscription-strip">
              <div className="subscription-item">
                <span className="subscription-label">Plan:</span>
                <span className="subscription-value highlight">
                  {user?.subscription?.plan?.charAt(0).toUpperCase() + user?.subscription?.plan?.slice(1) || 'Free'}
                </span>
              </div>
              <div className="subscription-divider"></div>
              <div className="subscription-item">
                <span className="subscription-label">Status:</span>
                <span className={`subscription-status ${user?.subscription?.status || 'active'}`}>
                  {user?.subscription?.status?.charAt(0).toUpperCase() + user?.subscription?.status?.slice(1) || 'Active'}
                </span>
              </div>
              <div className="subscription-divider"></div>
              <div className="subscription-item">
                <span className="subscription-label">Briefs:</span>
                {/* Calculate values here to avoid undefined variables */}
                {(() => {
                  const maxQueries = user?.subscription?.limits?.max_queries || 2;
                  const activeQueries = countActiveBriefs();
                  const inactiveQueries = countInactiveBriefs();
                  return (
                    <span className={`subscription-value ${activeQueries >= maxQueries ? 'count-limit-reached' : ''}`}>
                      {activeQueries} / {maxQueries} active ({inactiveQueries} inactive)
                    </span>
                  );
                })()}
              </div>
              <div className="subscription-divider"></div>
              <div className="subscription-item">
                <a href="/subscription" className="upgrade-button">
                  Upgrade Plan
                </a>
              </div>
            </div>
          </div>
        </header>
        
        {error && (
          <div className="error-alert">
            <div className="error-alert-message">{error}</div>
            <button 
              className="error-alert-close"
              onClick={() => setError(null)}
              aria-label="Close error message"
            >
              ×
            </button>
          </div>
        )}

        {/* Current Queries Section */}
        <section className="queries-section">
          <div className="section-header">
            <h2>Your Briefs</h2>
            <div className="section-actions">
              <button 
                onClick={refreshUserData}
                className="refresh-button"
                disabled={loading}
              >
                <span className="refresh-icon">↻</span> Refresh
              </button>
              <button 
                onClick={() => setShowNewBriefForm(!showNewBriefForm)}
                className={`toggle-form-button ${showNewBriefForm ? 'active' : ''}`}
                disabled={countActiveBriefs() >= (user?.subscription?.limits?.max_queries || 2)}
                title={countActiveBriefs() >= (user?.subscription?.limits?.max_queries || 2) ? 
                  `You have reached your limit of ${user?.subscription?.limits?.max_queries || 2} active briefs. Please disable an existing brief or upgrade your subscription.` : 
                  'Create a new brief'}
              >
                {showNewBriefForm ? 'Cancel' : countActiveBriefs() >= (user?.subscription?.limits?.max_queries || 2) ? 'Limit Reached' : '+ New Brief'}
              </button>
            </div>
          </div>
          
          {countActiveBriefs() >= (user?.subscription?.limits?.max_queries || 2) && queries.some(q => q.metadata?.status === 'inactive') && (
            <div className="info-alert">
              <div className="info-alert-message">
                You have reached your limit of {user?.subscription?.limits?.max_queries || 2} active briefs. 
                You can enable an inactive brief by disabling an active one first, or 
                <a href="/subscription" className="upgrade-link">upgrade your subscription</a> to increase your limit.
              </div>
            </div>
          )}
          
          {/* New Brief Quick Form */}
          {showNewBriefForm &&
            <div className="quick-form-container">
              <form onSubmit={handleAddQuery} className="quick-form">
                <div className="quick-form-fields">
                  <div className="form-group">
                    <label htmlFor="main_topic">Main Topic</label>
                    <input
                      type="text"
                      id="main_topic"
                      name="main_topic"
                      value={newBrief.main_topic}
                      onChange={handleNewBriefChange}
                      placeholder="What topic would you like to know about? (e.g. Technology News, Climate Change)"
                      className="quick-form-topic"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="persona">Persona</label>
                    <select
                      id="persona"
                      name="persona"
                      value={newBrief.persona}
                      onChange={handleNewBriefChange}
                      className="quick-form-select"
                      required
                    >
                      <option value="">Select a persona</option>
                      <option value="Professional">Professional</option>
                      <option value="Student">Student</option>
                      <option value="Hobbyist">Hobbyist</option>
                      <option value="Elementary">Elementary</option>
                      <option value="Financial Analyst">Financial Analyst</option>
                      <option value="Business Analyst">Business Analyst</option>
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="focus_areas">Focus Areas</label>
                      <div className="tag-input-container">
                        <div className="tag-list" aria-live="polite">
                          {newBrief.focus_areas.map((area, index) => (
                            <span key={index} className="tag">
                              {area}
                              <button
                                type="button"
                                onClick={() => handleRemoveFocusArea(index)}
                                className="tag-remove"
                                aria-label={`Remove ${area}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="tag-input-wrapper">
                          <input
                            type="text"
                            id="focus_areas"
                            name="focusAreaInput"
                            value={newBrief.focusAreaInput}
                            onChange={handleNewBriefChange}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddFocusArea(e);
                              }
                            }}
                            placeholder="Add a focus area"
                            className="tag-input"
                            aria-label="Add focus area"
                          />
                          <button
                            type="button"
                            onClick={(e) => handleAddFocusArea(e)}
                            className="tag-add-button"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="exclude_topics">Exclude Topics</label>
                      <div className="tag-input-container">
                        <div className="tag-list" aria-live="polite">
                          {newBrief.exclude_topics.map((topic, index) => (
                            <span key={index} className="tag">
                              {topic}
                              <button
                                type="button"
                                onClick={() => handleRemoveExcludeTopic(index)}
                                className="tag-remove"
                                aria-label={`Remove ${topic}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="tag-input-wrapper">
                          <input
                            type="text"
                            id="exclude_topics"
                            name="excludeTopicInput"
                            value={newBrief.excludeTopicInput}
                            onChange={handleNewBriefChange}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddExcludeTopic(e);
                              }
                            }}
                            placeholder="Add a topic to exclude"
                            className="tag-input"
                            aria-label="Add topic to exclude"
                          />
                          <button
                            type="button"
                            onClick={(e) => handleAddExcludeTopic(e)}
                            className="tag-add-button"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="time">Delivery Time</label>
                      <input
                        type="time"
                        id="time"
                        name="time_of_day"
                        value={newBrief.time_of_day}
                        onChange={handleNewBriefChange}
                        className="quick-form-time"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="timezone">Timezone</label>
                      <select
                        id="timezone"
                        name="timezone"
                        value={newBrief.timezone}
                        onChange={handleNewBriefChange}
                        className="quick-form-select"
                      >
                        {Intl.supportedValuesOf('timeZone').map(tz => (
                          <option key={tz} value={tz}>
                            {tz.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={() => setShowNewBriefForm(false)}
                    className="quick-form-cancel"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={loading}
                    className="quick-form-submit"
                  >
                    {loading ? 'Creating...' : 'Create Brief'}
                  </button>
                </div>
              </form>
            </div>
          }
          
          {loading && queries.length === 0 && <p className="loading-text">Brewing your briefs...</p>}
          
          {!loading && queries.length === 0 && !showNewBriefForm && (
            <div className="empty-state">
              <div className="empty-state-icon"></div>
              <p>You don't have any briefs yet.</p>
              <button 
                onClick={() => setShowNewBriefForm(true)}
                className="toggle-form-button"
                disabled={countActiveBriefs() >= (user?.subscription?.limits?.max_queries || 2)}
                title={countActiveBriefs() >= (user?.subscription?.limits?.max_queries || 2) ? 
                  `You have reached your limit of ${user?.subscription?.limits?.max_queries || 2} active briefs. Please disable an existing brief or upgrade your subscription.` : 
                  'Create your first brief'}
              >
                {countActiveBriefs() >= (user?.subscription?.limits?.max_queries || 2) ? 'Limit Reached' : 'Create your first brief'}
              </button>
            </div>
          )}
          
          {queries.length > 0 && (
            <div className="queries-list">
              {queries.map(query => (
                <div key={query.query_id} className={`query-item ${editingId === query.query_id ? 'editing' : ''} ${query.metadata?.status === 'inactive' ? 'inactive' : ''}`}>
                  {editingId === query.query_id ? (
                    <div className="query-edit-form">
                      <div className="form-group">
                        <label htmlFor={`edit-main-topic-${query.query_id}`}>Main Topic</label>
                        <input
                          type="text"
                          id={`edit-main-topic-${query.query_id}`}
                          value={query.editData.main_topic || ''}
                          onChange={(e) => handleEditChange(query.query_id, 'main_topic', e.target.value)}
                          className="query-edit-topic"
                          placeholder="What topic would you like to know about?"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor={`edit-persona-${query.query_id}`}>Persona</label>
                        <select
                          id={`edit-persona-${query.query_id}`}
                          value={query.editData.persona || ''}
                          onChange={(e) => handleEditChange(query.query_id, 'persona', e.target.value)}
                          className="query-edit-select"
                          required
                        >
                          <option value="">Select a persona</option>
                          <option value="Professional">Professional</option>
                          <option value="Student">Student</option>
                          <option value="Hobbyist">Hobbyist</option>
                          <option value="Elementary">Elementary</option>
                          <option value="Financial Analyst">Financial Analyst</option>
                          <option value="Business Analyst">Business Analyst</option>
                        </select>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor={`edit-focus-areas-${query.query_id}`}>Focus Areas</label>
                          <div className="tag-input-container">
                            <div className="tag-list">
                              {(query.editData.focus_areas || []).map((area, index) => (
                                <span key={index} className="tag">
                                  {area}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newAreas = [...(query.editData.focus_areas || [])];
                                      newAreas.splice(index, 1);
                                      handleEditChange(query.query_id, 'focus_areas', newAreas);
                                    }}
                                    className="tag-remove"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                            <div className="tag-input-wrapper">
                              <input
                                type="text"
                                id={`edit-focus-areas-${query.query_id}`}
                                value={query.editData.focusAreaInput || ''}
                                onChange={(e) => handleEditChange(query.query_id, 'focusAreaInput', e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddFocusArea(e, false, query.query_id);
                                  }
                                }}
                                placeholder="Type and press Enter to add"
                                className="tag-input"
                              />
                              <button
                                type="button"
                                onClick={(e) => handleAddFocusArea(e, false, query.query_id)}
                                className="tag-add-button"
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor={`edit-exclude-topics-${query.query_id}`}>Exclude Topics</label>
                          <div className="tag-input-container">
                            <div className="tag-list">
                              {(query.editData.exclude_topics || []).map((topic, index) => (
                                <span key={index} className="tag">
                                  {topic}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newTopics = [...(query.editData.exclude_topics || [])];
                                      newTopics.splice(index, 1);
                                      handleEditChange(query.query_id, 'exclude_topics', newTopics);
                                    }}
                                    className="tag-remove"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                            <div className="tag-input-wrapper">
                              <input
                                type="text"
                                id={`edit-exclude-topics-${query.query_id}`}
                                value={query.editData.excludeTopicInput || ''}
                                onChange={(e) => handleEditChange(query.query_id, 'excludeTopicInput', e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddExcludeTopic(e, false, query.query_id);
                                  }
                                }}
                                placeholder="Type and press Enter to add"
                                className="tag-input"
                              />
                              <button
                                type="button"
                                onClick={(e) => handleAddExcludeTopic(e, false, query.query_id)}
                                className="tag-add-button"
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor={`edit-time-${query.query_id}`}>Delivery Time</label>
                          <input
                            type="time"
                            id={`edit-time-${query.query_id}`}
                            value={query.editData.time_of_day || '08:00:00'}
                            onChange={(e) => handleEditChange(query.query_id, 'time_of_day', e.target.value)}
                            className="query-edit-time"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor={`edit-timezone-${query.query_id}`}>Timezone</label>
                          <select
                            id={`edit-timezone-${query.query_id}`}
                            value={query.editData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                            onChange={(e) => handleEditChange(query.query_id, 'timezone', e.target.value)}
                            className="query-edit-select"
                          >
                            {Intl.supportedValuesOf('timeZone').map(tz => (
                              <option key={tz} value={tz}>
                                {tz.replace(/_/g, ' ')}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor={`edit-status-${query.query_id}`}>Status</label>
                        <div className="status-toggle-container">
                          <label className="status-toggle">
                            <input
                              type="checkbox"
                              id={`edit-status-${query.query_id}`}
                              checked={query.editData.status !== 'inactive'}
                              onChange={(e) => {
                                // If we're enabling and we've reached the limit, show an error
                                if (e.target.checked) {
                                  const maxQueries = user?.subscription?.limits?.max_queries || 2;
                                  const activeQueries = countActiveBriefs();
                                  
                                  // Don't count this query if it's already active
                                  const adjustedCount = query.metadata?.status === 'inactive' ? activeQueries : activeQueries - 1;
                                  
                                  if (adjustedCount >= maxQueries) {
                                    setError(
                                      <div>
                                        You have reached your limit of {maxQueries} active briefs. Please <a href="#" onClick={() => setEditingId(null)} style={{ color: 'inherit', textDecoration: 'underline' }}>disable an existing brief</a> or <a href="/subscription" style={{ color: 'inherit', textDecoration: 'underline' }}>upgrade your subscription</a>.
                                      </div>
                                    );
                                    return;
                                  }
                                }
                                
                                handleEditChange(query.query_id, 'status', e.target.checked ? 'active' : 'inactive');
                              }}
                              className="status-toggle-input"
                            />
                            <span className="status-toggle-slider"></span>
                          </label>
                          <span className="status-toggle-label">
                            {query.editData.status === 'inactive' ? 'Disabled' : 'Enabled'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="query-edit-actions">
                        <button 
                          onClick={() => cancelEdit(query.query_id)}
                          className="quick-form-cancel"
                        >
                          Cancel
                        </button>
                        <button 
                          onClick={() => saveEdit(query.query_id)}
                          className="quick-form-submit"
                          disabled={loading}
                        >
                          {loading ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="query-content">
                        <h3 className="query-title">{query.topic.main}</h3>
                        <div className="query-meta">
                          {query.topic.persona && (
                            <span className="query-persona">{query.topic.persona}</span>
                          )}
                          {query.topic.focus_areas && query.topic.focus_areas.length > 0 && (
                            <span className="query-focus-areas">
                              {query.topic.focus_areas.join(', ')}
                            </span>
                          )}
                          {query.topic.exclude_topics && query.topic.exclude_topics.length > 0 && (
                            <span className="query-exclude">
                              Excluding: {query.topic.exclude_topics.join(', ')}
                            </span>
                          )}
                          <span className="query-time">
                            {query.schedule.time_of_day} {query.schedule.timezone}
                          </span>
                        </div>
                      </div>
                      <div className="query-actions">
                        <button 
                          onClick={() => toggleQueryStatus(query.query_id, query.metadata?.status === 'inactive' ? 'active' : 'inactive')}
                          className={`query-action-button toggle-button ${query.metadata?.status === 'inactive' ? 'inactive' : 'active'}`}
                          aria-label={query.metadata?.status === 'inactive' ? 'Enable brief' : 'Disable brief'}
                          title={query.metadata?.status === 'inactive' ? 'Enable brief' : 'Disable brief'}
                        >
                          <span className="toggle-icon">{query.metadata?.status === 'inactive' ? '○' : '●'}</span>
                          <span className="toggle-text">{query.metadata?.status === 'inactive' ? 'Disabled' : 'Enabled'}</span>
                        </button>
                        <button 
                          onClick={() => toggleEditMode(query.query_id)}
                          className="query-action-button edit-button"
                          aria-label="Edit brief"
                        >
                          ✎
                        </button>
                        <button 
                          onClick={() => handleRemoveQuery(query.query_id)}
                          className="query-action-button remove-button"
                          aria-label="Remove brief"
                        >
                          ×
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export { Dashboard }; 